"use client";

/**
 * Shared hook for dropdown menu keyboard navigation and auto-focus
 * Handles ArrowUp/ArrowDown/Tab/Home/End/Escape key navigation
 * Used by SortableImage, FaqItemRow, and SectionOptionsMenu
 */

import type React from "react";
import { useCallback, useEffect, useRef } from "react";

interface UseMenuKeyboardNavOptions {
  /** Whether the menu is currently open */
  isOpen: boolean;
  /** Callback to close the menu (called on Escape) */
  onClose?: () => void;
}

interface UseMenuKeyboardNavReturn {
  /** Ref to attach to the menu content wrapper (contains the menuitem buttons) */
  menuContentRef: React.RefObject<HTMLDivElement>;
  /** KeyDown handler to attach to the menu content wrapper */
  handleMenuKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Provides keyboard navigation for dropdown menus following WAI-ARIA menu pattern
 * Auto-focuses the first enabled menuitem on open, navigates with arrow keys
 * @param options - Configuration including open state and close callback
 * @returns Ref and keydown handler for the menu content wrapper
 */
export function useMenuKeyboardNav({ isOpen, onClose }: UseMenuKeyboardNavOptions): UseMenuKeyboardNavReturn {
  const menuContentRef = useRef<HTMLDivElement>(null);

  // Auto-focus first enabled menu item when menu opens
  useEffect(() => {
    if (!isOpen) return;
    const firstEnabled = menuContentRef.current?.querySelector<HTMLButtonElement>(
      'button[role="menuitem"]:not(:disabled)'
    );
    firstEnabled?.focus();
  }, [isOpen]);

  // Arrow key navigation
  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const items = Array.from(
        menuContentRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)') ?? []
      );
      const currentIndex = items.indexOf(e.target as HTMLButtonElement);

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = currentIndex + 1 < items.length ? currentIndex + 1 : 0;
        items[next]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = currentIndex - 1 >= 0 ? currentIndex - 1 : items.length - 1;
        items[prev]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        items[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        items[items.length - 1]?.focus();
      } else if (e.key === "Tab") {
        e.preventDefault();
        if (e.shiftKey) {
          const prev = currentIndex - 1 >= 0 ? currentIndex - 1 : items.length - 1;
          items[prev]?.focus();
        } else {
          const next = currentIndex + 1 < items.length ? currentIndex + 1 : 0;
          items[next]?.focus();
        }
      } else if (e.key === "Escape") {
        onClose?.();
      }
    },
    [onClose]
  );

  return { menuContentRef, handleMenuKeyDown };
}
